import React, { useState } from 'react';
import { GripVertical, Plus, Edit2, Trash2, X, Check, MoreVertical, ChevronLeft, ChevronRight, Calendar, AlertCircle, FileText, Tag, Table, List, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { IconRenderer } from '../../lib/icon-map';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { DatePicker } from '../ui/date-picker';
import { parseISO, format } from 'date-fns';

interface KanbanCard {
  id: string;
  title: string;
  description?: string;
  badge?: string;
  color?: string;
  date?: string;
  priority?: 'low' | 'medium' | 'high';
}

interface KanbanColumn {
  id: string;
  title: string;
  icon?: string;
  cards: KanbanCard[];
}

interface KanbanWidgetProps {
  title?: string;
  columns: KanbanColumn[];
  onCardsChange?: (columns: KanbanColumn[]) => void;
}

export default function KanbanWidget({
  title,
  columns: initialColumns,
  onCardsChange,
}: KanbanWidgetProps) {
  const [columns, setColumns] = useState<KanbanColumn[]>(initialColumns || []);
  const [draggedCard, setDraggedCard] = useState<{
    card: KanbanCard;
    fromColumnId: string;
  } | null>(null);
  const [dragOverCard, setDragOverCard] = useState<{
    cardId: string;
    columnId: string;
  } | null>(null);
  const [editingCard, setEditingCard] = useState<{
    columnId: string;
    cardId: string;
  } | null>(null);
  const [visibleFields, setVisibleFields] = useState<{
    description: boolean;
    date: boolean;
    priority: boolean;
    badge: boolean;
  }>({ description: false, date: false, priority: false, badge: false });
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(new Set());
  const [editColumnForm, setEditColumnForm] = useState<{ title: string; icon: string }>({
    title: '',
    icon: '',
  });
  const [editForm, setEditForm] = useState<KanbanCard>({
    id: '',
    title: '',
    description: '',
    badge: '',
    color: 'blue',
  });
  const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');
  const [collapsedTableGroups, setCollapsedTableGroups] = useState<Set<string>>(new Set());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleDragStart = (card: KanbanCard, columnId: string) => {
    setDraggedCard({ card, fromColumnId: columnId });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleCardDragOver = (e: React.DragEvent, cardId: string, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedCard && draggedCard.card.id !== cardId) {
      setDragOverCard({ cardId, columnId });
    }
  };

  const handleCardDrop = (e: React.DragEvent, targetCardId: string, targetColumnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedCard) return;

    const newColumns = columns.map((col) => {
      // Remover o card da coluna de origem
      const cardsWithoutDragged = col.cards.filter((c) => c.id !== draggedCard.card.id);
      
      if (col.id === targetColumnId) {
        // Encontrar o índice do card alvo
        const targetIndex = cardsWithoutDragged.findIndex((c) => c.id === targetCardId);
        
        // Inserir o card arrastado na posição do card alvo
        const newCards = [...cardsWithoutDragged];
        newCards.splice(targetIndex, 0, draggedCard.card);
        
        return {
          ...col,
          cards: newCards,
        };
      }
      
      return {
        ...col,
        cards: cardsWithoutDragged,
      };
    });

    setColumns(newColumns);
    if (onCardsChange) {
      onCardsChange(newColumns);
    }
    setDraggedCard(null);
    setDragOverCard(null);
  };

  const handleDrop = (toColumnId: string) => {
    if (!draggedCard) return;

    // Se for a mesma coluna e não houve drop em um card específico, manter na posição original
    if (draggedCard.fromColumnId === toColumnId) {
      setDraggedCard(null);
      return;
    }

    const newColumns = columns.map((col) => {
      // Remover o card da coluna de origem
      if (col.id === draggedCard.fromColumnId) {
        return {
          ...col,
          cards: col.cards.filter((c) => c.id !== draggedCard.card.id),
        };
      }
      // Adicionar o card na coluna de destino (no final)
      if (col.id === toColumnId) {
        return {
          ...col,
          cards: [...col.cards, draggedCard.card],
        };
      }
      return col;
    });

    setColumns(newColumns);
    if (onCardsChange) {
      onCardsChange(newColumns);
    }
    setDraggedCard(null);
  };

  const handleAddCard = (columnId: string, addToTop: boolean = false, skipEdit: boolean = false) => {
    const newCard: KanbanCard = {
      id: `card-${Date.now()}`,
      title: 'Novo Card',
      description: '',
      badge: '',
      color: 'blue',
    };

    const newColumns = columns.map((col) => {
      if (col.id === columnId) {
        return {
          ...col,
          cards: addToTop ? [newCard, ...col.cards] : [...col.cards, newCard],
        };
      }
      return col;
    });

    setColumns(newColumns);
    if (onCardsChange) {
      onCardsChange(newColumns);
    }

    if (!skipEdit) {
      // Iniciar edição imediatamente
      setEditingCard({ columnId, cardId: newCard.id });
      setEditForm(newCard);
      // Resetar campos visíveis para novo card
      setVisibleFields({
        description: false,
        date: false,
        priority: false,
        badge: false,
      });
      
      if (viewMode === 'table') {
        setIsEditModalOpen(true);
      }
    }
  };

  const handleEditCard = (columnId: string, card: KanbanCard) => {
    setEditingCard({ columnId, cardId: card.id });
    setEditForm({ ...card });
    setVisibleFields({
      description: !!card.description,
      date: !!card.date,
      priority: !!card.priority,
      badge: !!card.badge,
    });
    
    if (viewMode === 'table') {
      setIsEditModalOpen(true);
    }
  };

  const handleSaveCard = () => {
    if (!editingCard) return;

    const newColumns = columns.map((col) => {
      if (col.id === editingCard.columnId) {
        return {
          ...col,
          cards: col.cards.map((card) =>
            card.id === editingCard.cardId ? editForm : card
          ),
        };
      }
      return col;
    });

    setColumns(newColumns);
    if (onCardsChange) {
      onCardsChange(newColumns);
    }
    
    if (viewMode === 'table') {
      setIsEditModalOpen(false);
    }
    setEditingCard(null);
  };

  const handleDeleteCard = (columnId: string, cardId: string) => {
    const newColumns = columns.map((col) => {
      if (col.id === columnId) {
        return {
          ...col,
          cards: col.cards.filter((card) => card.id !== cardId),
        };
      }
      return col;
    });

    setColumns(newColumns);
    if (onCardsChange) {
      onCardsChange(newColumns);
    }
  };

  const handleCancelEdit = () => {
    if (viewMode === 'table') {
      setIsEditModalOpen(false);
    }
    setEditingCard(null);
  };

  const handleAddColumn = () => {
    const newColumn: KanbanColumn = {
      id: `col-${Date.now()}`,
      title: 'Nova Coluna',
      icon: '📋',
      cards: [],
    };

    const newColumns = [...columns, newColumn];
    setColumns(newColumns);
    if (onCardsChange) {
      onCardsChange(newColumns);
    }
  };

  const handleEditColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column) {
      setEditingColumn(columnId);
      setEditColumnForm({ title: column.title, icon: column.icon || '' });
    }
  };

  const handleSaveColumn = () => {
    if (!editingColumn) return;

    const newColumns = columns.map((col) => {
      if (col.id === editingColumn) {
        return {
          ...col,
          title: editColumnForm.title,
          icon: editColumnForm.icon,
        };
      }
      return col;
    });

    setColumns(newColumns);
    if (onCardsChange) {
      onCardsChange(newColumns);
    }
    setEditingColumn(null);
  };

  const handleDeleteColumn = (columnId: string) => {
    if (confirm('Tem certeza que deseja deletar esta coluna?')) {
      const newColumns = columns.filter(col => col.id !== columnId);
      setColumns(newColumns);
      if (onCardsChange) {
        onCardsChange(newColumns);
      }
    }
  };

  const toggleColumnCollapse = (columnId: string) => {
    setCollapsedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnId)) {
        newSet.delete(columnId);
      } else {
        newSet.add(columnId);
      }
      return newSet;
    });
  };

  const toggleTableGroupCollapse = (columnId: string) => {
    setCollapsedTableGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnId)) {
        newSet.delete(columnId);
      } else {
        newSet.add(columnId);
      }
      return newSet;
    });
  };

  return (
    <Card data-widget="kanban" className="h-full flex flex-col overflow-hidden">
      {title && (
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="truncate">{title}</CardTitle>
            <div className="flex items-center gap-2">
              <div className="flex bg-muted rounded-lg p-1">
                <Button
                  variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('kanban')}
                  className={`rounded-md transition-all ${
                    viewMode === 'kanban' 
                      ? 'border-primary border-2 bg-muted/30 text-foreground/90 shadow-sm' 
                      : 'hover:bg-background/50'
                  }`}
                >
                  <List className="w-4 h-4 mr-2" />
                  Kanban
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className={`rounded-md transition-all ${
                    viewMode === 'table' 
                      ? 'border-primary border-2 bg-muted/30 text-foreground/90 shadow-sm' 
                      : 'hover:bg-background/50'
                  }`}
                >
                  <Table className="w-4 h-4 mr-2" />
                  Tabela
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
      )}

      <CardContent className="flex-1 overflow-auto p-4">
        {viewMode === 'kanban' ? (
          <div className="flex gap-4 items-start min-w-min h-full">
            {columns.map((column) => {
              const isCollapsed = collapsedColumns.has(column.id);
              
              return (
              <div
                key={column.id}
                className={`flex-shrink-0 bg-muted/30 rounded-lg p-4 flex flex-col transition-all ${
                  isCollapsed ? 'w-16' : 'w-80 min-w-[280px]'
                }`}
                onDragOver={!isCollapsed ? handleDragOver : undefined}
                onDrop={!isCollapsed ? () => handleDrop(column.id) : undefined}
              >
                {isCollapsed ? (
                  /* Coluna Recolhida - Modo Vertical */
                  <div className="flex flex-col items-center gap-3 min-h-[200px]">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleColumnCollapse(column.id)}
                      className="h-8 w-8"
                      title="Expandir coluna"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    {column.icon && (
                      <IconRenderer icon={column.icon} className="w-6 h-6" />
                    )}
                    <div className="writing-mode-vertical text-sm font-semibold whitespace-nowrap" style={{ writingMode: 'vertical-rl' }}>
                      {column.title}
                    </div>
                    <Badge variant="outline" className="text-xs mt-auto">
                      {column.cards.length}
                    </Badge>
                  </div>
                ) : (
                  /* Coluna Expandida - Modo Normal */
                  <>
                <div className="mb-3">
                  {editingColumn === column.id ? (
                    <div className="space-y-2 mb-2">
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Ícone (emoji)"
                          value={editColumnForm.icon}
                          onChange={(e) => setEditColumnForm({ ...editColumnForm, icon: e.target.value })}
                          className="w-16 text-center"
                        />
                        <Input
                          type="text"
                          placeholder="Título da coluna"
                          value={editColumnForm.title}
                          onChange={(e) => setEditColumnForm({ ...editColumnForm, title: e.target.value })}
                          className="flex-1"
                          autoFocus
                        />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingColumn(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleSaveColumn}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleColumnCollapse(column.id)}
                          className="h-7 w-7 -ml-1"
                          title="Recolher coluna"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        {column.icon && <IconRenderer icon={column.icon} className="w-5 h-5" />}
                        <h4 className="font-semibold">{column.title}</h4>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="text-xs">
                          {column.cards.length}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditColumn(column.id)}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteColumn(column.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Deletar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  )}
                  
                  {/* Add Card Button - Logo abaixo do título */}
                  <Button
                    variant="ghost"
                    onClick={() => handleAddCard(column.id)}
                    className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-accent/50 h-8 px-2"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    <span className="text-sm">Adicionar card</span>
                  </Button>
                </div>

                {/* Cards */}
                <div className="space-y-2">
                  {column.cards.map((card) => {
                    const isEditing = editingCard?.columnId === column.id && editingCard?.cardId === card.id;

                    if (isEditing) {
                      return (
                        <Card
                          key={card.id}
                          className="border-2 border-primary"
                        >
                          <CardContent className="p-3 space-y-2">
                            <Input
                              type="text"
                              placeholder="Título do card"
                              value={editForm.title}
                              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                              autoFocus
                            />

                            {/* Campos Opcionais */}
                            {visibleFields.description && (
                              <Textarea
                                placeholder="Descrição"
                                value={editForm.description || ''}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                rows={2}
                              />
                            )}
                            
                            {visibleFields.date && (
                              <DatePicker
                                date={editForm.date ? parseISO(editForm.date) : undefined}
                                onDateChange={(date) => setEditForm({ ...editForm, date: date ? format(date, "yyyy-MM-dd") : "" })}
                                placeholder="Select date"
                              />
                            )}
                            
                            {visibleFields.priority && (
                              <Select
                                value={editForm.priority || 'medium'}
                                onValueChange={(value: 'low' | 'medium' | 'high') => setEditForm({ ...editForm, priority: value })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Prioridade" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="high">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-red-500" />
                                      <span>Alta</span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="medium">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-yellow-500" />
                                      <span>Média</span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="low">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-green-500" />
                                      <span>Baixa</span>
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            
                            {visibleFields.badge && (
                              <Input
                                type="text"
                                placeholder="Badge/Tag"
                                value={editForm.badge || ''}
                                onChange={(e) => setEditForm({ ...editForm, badge: e.target.value })}
                              />
                            )}
                            
                            {/* Ícones de Atalho - No final */}
                            <div className="flex gap-1 pt-2 border-t">
                              <Button
                                type="button"
                                variant={visibleFields.description ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => setVisibleFields({ ...visibleFields, description: !visibleFields.description })}
                                title="Descrição"
                                className="h-8 px-2"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant={visibleFields.date ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => setVisibleFields({ ...visibleFields, date: !visibleFields.date })}
                                title="Data"
                                className="h-8 px-2"
                              >
                                <Calendar className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant={visibleFields.priority ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => {
                                  const newVisible = !visibleFields.priority;
                                  setVisibleFields({ ...visibleFields, priority: newVisible });
                                  // Se estiver ativando o campo e não há prioridade definida, definir como média
                                  if (newVisible && !editForm.priority) {
                                    setEditForm({ ...editForm, priority: 'medium' });
                                  }
                                }}
                                title="Prioridade"
                                className="h-8 px-2"
                              >
                                <AlertCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant={visibleFields.badge ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => setVisibleFields({ ...visibleFields, badge: !visibleFields.badge })}
                                title="Badge"
                                className="h-8 px-2"
                              >
                                <Tag className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            <div className="flex gap-2 justify-end pt-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelEdit}
                                title="Cancelar"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={handleSaveCard}
                                title="Salvar"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }

                    return (
                      <Card
                        key={card.id}
                        draggable
                        onDragStart={() => handleDragStart(card, column.id)}
                        onDragOver={(e) => handleCardDragOver(e, card.id, column.id)}
                        onDrop={(e) => handleCardDrop(e, card.id, column.id)}
                        className={`cursor-move hover:shadow-md transition-all group/card ${
                          dragOverCard?.cardId === card.id && dragOverCard?.columnId === column.id
                            ? 'border-primary border-2'
                            : ''
                        }`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start gap-2">
                            <GripVertical className="w-4 h-4 text-muted-foreground mt-0.5 opacity-0 group-hover/card:opacity-100 transition-opacity flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h5 className="font-medium text-sm mb-1">
                                {card.title}
                              </h5>
                              {card.description && (
                                <p className="text-xs text-muted-foreground mb-2">
                                  {card.description}
                                </p>
                              )}
                              <div className="flex flex-wrap gap-1.5 items-center">
                                {card.priority && (
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs px-1.5 py-0 border ${
                                      card.priority === 'high' ? 'bg-red-500/10 border-red-500/50 text-red-700 dark:text-red-400' :
                                      card.priority === 'medium' ? 'bg-yellow-500/10 border-yellow-500/50 text-yellow-700 dark:text-yellow-400' :
                                      'bg-green-500/10 border-green-500/50 text-green-700 dark:text-green-400'
                                    }`}
                                  >
                                    <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                                      card.priority === 'high' ? 'bg-red-500' :
                                      card.priority === 'medium' ? 'bg-yellow-500' :
                                      'bg-green-500'
                                    }`} />
                                    {card.priority === 'high' ? 'Alta' : card.priority === 'medium' ? 'Média' : 'Baixa'}
                                  </Badge>
                                )}
                                {card.date && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {new Date(card.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                  </Badge>
                                )}
                                {card.badge && (
                                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                    {card.badge}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditCard(column.id, card)}
                                title="Editar"
                                className="h-6 w-6 p-0"
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCard(column.id, card.id)}
                                title="Excluir"
                                className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {column.cards.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhum card
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
              );
            })}

            {/* Botão Adicionar Coluna */}
            <div className="flex-shrink-0 w-80">
              <Button
                variant="outline"
                onClick={handleAddColumn}
                className="w-full h-full min-h-[200px] border-2 border-dashed hover:border-primary hover:bg-accent/50 transition-all"
              >
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Plus className="w-8 h-8" />
                  <span className="font-medium">Adicionar Coluna</span>
                </div>
              </Button>
            </div>
          </div>
        ) : (
          /* Table View */
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Cards</h3>
              {(viewMode as 'kanban' | 'table') === 'kanban' && (
                <Button onClick={() => handleAddCard(columns[0]?.id || '')} disabled={!columns.length}>
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Card
                </Button>
              )}
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Título</th>
                    <th className="px-4 py-2 text-left font-medium">Descrição</th>
                    <th className="px-4 py-2 text-left font-medium">Data</th>
                    <th className="px-4 py-2 text-left font-medium">Prioridade</th>
                    <th className="px-4 py-2 text-left font-medium">Badge</th>
                    <th className="px-4 py-2 text-left font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map(column => {
                    const isCollapsed = collapsedTableGroups.has(column.id);
                    return (
                    <React.Fragment key={column.id}>
                      <tr className="bg-muted/20 cursor-pointer hover:bg-muted/30 group" onClick={() => toggleTableGroupCollapse(column.id)}>
                        <td colSpan={6} className="px-4 py-2 font-semibold text-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              {column.icon && <IconRenderer icon={column.icon} className="w-4 h-4" />}
                              {column.title}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddCard(column.id, true, false);
                                }}
                                className="h-5 w-5 p-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Adicionar card"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                              <Badge variant="outline" className="text-xs">
                                {column.cards.length}
                              </Badge>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {!isCollapsed && (
                        <>
                          {column.cards.map(card => (
                            <tr key={card.id} className="border-t border-gray-700/10 hover:bg-muted/30">
                              <td className="px-4 py-0.5 pl-12">{card.title}</td>
                              <td className="px-4 py-0.5 text-sm text-muted-foreground max-w-xs truncate">
                                {card.description || '-'}
                              </td>
                              <td className="px-4 py-0.5 text-sm">
                                {card.date ? new Date(card.date).toLocaleDateString('pt-BR') : '-'}
                              </td>
                              <td className="px-4 py-0.5">
                                {card.priority ? (
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs ${
                                      card.priority === 'high' ? 'bg-red-500/10 border-red-500/50 text-red-700 dark:text-red-400' :
                                      card.priority === 'medium' ? 'bg-yellow-500/10 border-yellow-500/50 text-yellow-700 dark:text-yellow-400' :
                                      'bg-green-500/10 border-green-500/50 text-green-700 dark:text-green-400'
                                    }`}
                                  >
                                    <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                                      card.priority === 'high' ? 'bg-red-500' :
                                      card.priority === 'medium' ? 'bg-yellow-500' :
                                      'bg-green-500'
                                    }`} />
                                    {card.priority === 'high' ? 'Alta' : card.priority === 'medium' ? 'Média' : 'Baixa'}
                                  </Badge>
                                ) : '-'}
                              </td>
                              <td className="px-4 py-0.5">
                                {card.badge ? <Badge variant="secondary" className="text-xs">{card.badge}</Badge> : '-'}
                              </td>
                              <td className="px-4 py-0.5">
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditCard(column.id, card)}
                                    title="Editar"
                                    className="h-8 w-8 p-0"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteCard(column.id, card.id)}
                                    title="Excluir"
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {column.cards.length === 0 && (
                            <tr>
                              <td colSpan={6} className="px-4 py-4 text-center text-muted-foreground text-sm border-t">
                                Nenhum card nesta coluna
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </React.Fragment>
                    );
                  })}
                  {columns.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhuma coluna encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>

      {/* Modal de Edição para Modo Tabela */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Editar Card</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="text"
              placeholder="Título do card"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              autoFocus
            />

            {/* Campos Opcionais */}
            {visibleFields.description && (
              <Textarea
                placeholder="Descrição"
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
              />
            )}
            
            {visibleFields.date && (
              <DatePicker
                date={editForm.date ? parseISO(editForm.date) : undefined}
                onDateChange={(date) => setEditForm({ ...editForm, date: date ? format(date, "yyyy-MM-dd") : "" })}
                placeholder="Selecionar data"
              />
            )}
            
            {visibleFields.priority && (
              <Select
                value={editForm.priority || 'medium'}
                onValueChange={(value: 'low' | 'medium' | 'high') => setEditForm({ ...editForm, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span>Alta</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-yellow-500" />
                      <span>Média</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span>Baixa</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            
            {visibleFields.badge && (
              <Input
                type="text"
                placeholder="Badge/Tag"
                value={editForm.badge || ''}
                onChange={(e) => setEditForm({ ...editForm, badge: e.target.value })}
              />
            )}
            
            {/* Ícones de Atalho */}
            <div className="flex gap-1 pt-2 border-t">
              <Button
                type="button"
                variant={visibleFields.description ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setVisibleFields({ ...visibleFields, description: !visibleFields.description })}
                title="Descrição"
                className="h-8 px-2"
              >
                <FileText className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant={visibleFields.date ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setVisibleFields({ ...visibleFields, date: !visibleFields.date })}
                title="Data"
                className="h-8 px-2"
              >
                <Calendar className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant={visibleFields.priority ? "secondary" : "ghost"}
                size="sm"
                onClick={() => {
                  const newVisible = !visibleFields.priority;
                  setVisibleFields({ ...visibleFields, priority: newVisible });
                  // Se estiver ativando o campo e não há prioridade definida, definir como média
                  if (newVisible && !editForm.priority) {
                    setEditForm({ ...editForm, priority: 'medium' });
                  }
                }}
                title="Prioridade"
                className="h-8 px-2"
              >
                <AlertCircle className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant={visibleFields.badge ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setVisibleFields({ ...visibleFields, badge: !visibleFields.badge })}
                title="Badge"
                className="h-8 px-2"
              >
                <Tag className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex gap-2 justify-end pt-4">
              <Button
                variant="outline"
                onClick={handleCancelEdit}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveCard}
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
